import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Filter, Loader, ShoppingCart, Heart, Scale, Shield, Zap, Cog, Settings, Bike, Sticker } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useCompare } from '../context/CompareContext';
import { getProxiedImageUrl, getProductPath, matchesProductSearch, scoreProductSearch } from '../utils/helpers';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import BikeFilter from '../components/BikeFilter';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Custom SVG Icons - All in KTM Orange (#FF6600)
const BrakeIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#FF6600" strokeWidth="2">
    <circle cx="16" cy="16" r="12" />
    <circle cx="16" cy="16" r="7" />
    <path d="M 8 10 L 4 6 M 24 10 L 28 6" strokeLinecap="round"/>
    <rect x="3" y="14" width="6" height="4" rx="1" fill="#FF6600"/>
    <rect x="23" y="14" width="6" height="4" rx="1" fill="#FF6600"/>
  </svg>
);

const SuspensionIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#FF6600" strokeWidth="2">
    <line x1="12" y1="6" x2="12" y2="26" strokeWidth="3"/>
    <line x1="20" y1="6" x2="20" y2="26" strokeWidth="3"/>
    <rect x="8" y="4" width="16" height="6" rx="1" fill="#FF6600" opacity="0.3"/>
    <circle cx="12" cy="18" r="2" fill="#FF6600"/>
    <circle cx="20" cy="18" r="2" fill="#FF6600"/>
    <path d="M 8 12 L 8 14 M 24 12 L 24 14" strokeWidth="2" strokeLinecap="round"/>
    <path d="M 8 20 L 8 22 M 24 20 L 24 22" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const PlasticFenderIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#FF6600" strokeWidth="2">
    <path d="M 6 20 Q 6 12 16 8 Q 26 12 26 20" fill="#FF6600" opacity="0.2"/>
    <path d="M 6 20 Q 6 12 16 8 Q 26 12 26 20" strokeLinecap="round"/>
    <path d="M 16 8 L 16 26" stroke="#FF6600" strokeWidth="1.5" strokeDasharray="2,2"/>
    <ellipse cx="16" cy="20" rx="10" ry="3" fill="#FF6600" opacity="0.3"/>
    <path d="M 10 14 Q 10 12 16 10 Q 22 12 22 14" strokeWidth="1.5"/>
  </svg>
);

const ExhaustIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#FF6600" strokeWidth="2">
    <rect x="4" y="12" width="20" height="8" rx="2"/>
    <path d="M 24 14 L 28 14 L 28 18 L 24 18" fill="#FF6600"/>
    <line x1="8" y1="16" x2="20" y2="16" strokeWidth="1"/>
    <circle cx="6" cy="16" r="1" fill="#FF6600"/>
    <circle cx="10" cy="16" r="1" fill="#FF6600"/>
    <circle cx="14" cy="16" r="1" fill="#FF6600"/>
  </svg>
);

const RiderGearIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#FF6600" strokeWidth="2">
    <circle cx="16" cy="8" r="3" fill="#FF6600"/>
    <path d="M 16 11 L 16 20 M 10 14 L 16 16 L 22 14" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M 16 20 L 12 28 M 16 20 L 20 28" strokeLinecap="round"/>
    <rect x="14" y="16" width="4" height="6" rx="1" fill="#FF6600" opacity="0.3"/>
  </svg>
);

const HelmetCrossIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#FF6600" strokeWidth="2">
    <path d="M 8 18 Q 8 8 16 4 Q 24 8 24 18 L 24 20 L 8 20 Z" fill="#FF6600" opacity="0.3"/>
    <path d="M 8 18 Q 8 8 16 4 Q 24 8 24 18" strokeLinecap="round"/>
    <line x1="8" y1="20" x2="24" y2="20" strokeWidth="3"/>
    <path d="M 10 12 L 16 10 L 22 12 L 22 15 L 10 15 Z" fill="none" stroke="#FF6600" strokeWidth="1.5"/>
    <path d="M 10 20 L 12 24 L 20 24 L 22 20" strokeWidth="2" strokeLinejoin="round"/>
  </svg>
);

const OilCanIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#FF6600" strokeWidth="2">
    <rect x="8" y="6" width="16" height="4" rx="1" fill="#FF6600"/>
    <path d="M 10 10 L 10 24 Q 10 26 12 26 L 20 26 Q 22 26 22 24 L 22 10" fill="#FF6600" opacity="0.3"/>
    <path d="M 10 10 L 10 24 Q 10 26 12 26 L 20 26 Q 22 26 22 24 L 22 10"/>
    <rect x="14" y="14" width="4" height="8" rx="1" fill="#FF6600" opacity="0.5"/>
    <line x1="12" y1="18" x2="20" y2="18" strokeWidth="1.5"/>
    <path d="M 20 8 L 24 8 Q 26 8 26 10 L 26 12" strokeLinecap="round"/>
  </svg>
);

const TireIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#FF6600" strokeWidth="2">
    <circle cx="16" cy="16" r="13" />
    <circle cx="16" cy="16" r="6" fill="#FF6600" opacity="0.25"/>
    <circle cx="16" cy="16" r="6" />
    <path d="M 16 3 L 16 7 M 16 25 L 16 29 M 3 16 L 7 16 M 25 16 L 29 16" strokeLinecap="round" strokeWidth="2.2"/>
    <path d="M 6.8 6.8 L 9.6 9.6 M 22.4 22.4 L 25.2 25.2 M 6.8 25.2 L 9.6 22.4 M 22.4 9.6 L 25.2 6.8" strokeLinecap="round" strokeWidth="1.5"/>
  </svg>
);

const LeverIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#FF6600" strokeWidth="2">
    <path d="M 4 22 Q 4 20 6 20 L 24 8 Q 27 6 28 9 L 27 11 Q 26 12 24 12 L 8 24 Q 5 25 4 22 Z" fill="#FF6600" opacity="0.25"/>
    <path d="M 4 22 Q 4 20 6 20 L 24 8 Q 27 6 28 9 L 27 11 Q 26 12 24 12 L 8 24 Q 5 25 4 22 Z" strokeLinejoin="round"/>
    <circle cx="6" cy="22" r="2" fill="#FF6600"/>
    <path d="M 22 10 L 26 12" strokeWidth="1.2"/>
  </svg>
);

const AirFilterIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#FF6600" strokeWidth="2">
    <rect x="6" y="8" width="20" height="16" rx="2" fill="#FF6600" opacity="0.2"/>
    <rect x="6" y="8" width="20" height="16" rx="2"/>
    <path d="M 6 12 L 26 12 M 6 16 L 26 16 M 6 20 L 26 20" strokeWidth="1.2"/>
    <path d="M 9 8 L 9 24 M 13 8 L 13 24 M 17 8 L 17 24 M 21 8 L 21 24 M 25 8 L 25 24" strokeWidth="0.8" opacity="0.6"/>
    <circle cx="16" cy="16" r="2.5" fill="#FF6600"/>
  </svg>
);

const Products = () => {
  const { t, language } = useLanguage();
  const { theme } = useTheme();
  const { addToCart } = useCart();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const { addToCompare, removeFromCompare, isInCompare } = useCompare();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewResults, setPreviewResults] = useState([]);
  const [sortBy, setSortBy] = useState(''); // New: sorting state
  const [bikeFilter, setBikeFilter] = useState({ brand: '', year: '', model: '' });
  const [bikeCompatibleSkus, setBikeCompatibleSkus] = useState(null); // null = no filter, array = restrict to these SKUs
  const navigate = useNavigate();

  // Comprehensive category list with CUSTOM ICONS
  // Categorie ordinate alfabeticamente
  const allCategories = [
    { name: 'Abbigliamento', name_en: 'Apparel', name_es: 'Ropa', icon: RiderGearIcon },
    { name: 'Adesivi', name_en: 'Stickers', name_es: 'Adhesivos', icon: Sticker },
    { name: 'Filtri aria', name_en: 'Air Filters', name_es: 'Filtros de aire', icon: AirFilterIcon },
    { name: 'Freni', name_en: 'Brakes', name_es: 'Frenos', icon: BrakeIcon },
    { name: 'Leve', name_en: 'Levers', name_es: 'Palancas', icon: LeverIcon },
    { name: 'Lubrificanti', name_en: 'Lubricants', name_es: 'Lubricantes', icon: OilCanIcon },
    { name: 'Parti elettriche', name_en: 'Electronics', name_es: 'Eléctrica', icon: Zap },
    { name: 'Parti motore', name_en: 'Engine Parts', name_es: 'Motor', icon: Settings },
    { name: 'Plastiche', name_en: 'Plastics', name_es: 'Plásticos', icon: PlasticFenderIcon },
    { name: 'Pneumatici', name_en: 'Tires', name_es: 'Neumáticos', icon: TireIcon },
    { name: 'Protezioni moto', name_en: 'Protection', name_es: 'Protecciones', icon: Shield },
    { name: 'Ruote', name_en: 'Wheels', name_es: 'Ruedas', icon: Bike },
    { name: 'Sospensioni', name_en: 'Suspensions', name_es: 'Suspensiones', icon: SuspensionIcon },
    { name: 'Trasmissione', name_en: 'Transmission', name_es: 'Transmisión', icon: Cog },
  ];

  const [productCounts, setProductCounts] = useState({});

  useEffect(() => {
    // Sync search term with the URL query string.
    // Reading it here (instead of only from useState default) makes:
    //   - Deep-links like /products?search=casco work
    //   - Back-button after clicking a product return to the same search
    //     (previously the URL was just /products and the search state was
    //     lost, showing the full catalog again — the reported bug).
    const urlSearch = searchParams.get('search') || '';
    setSearchTerm(urlSearch);
  }, [searchParams]);

  // Persist the current searchTerm back into the URL so that navigating to a
  // product and clicking "Back" restores the filtered list. `replace: true`
  // prevents every keystroke from adding a new entry to browser history.
  const handleSearchTermChange = (value) => {
    setSearchTerm(value);
    setSearchParams(
      value ? { search: value } : {},
      { replace: true },
    );
  };

  useEffect(() => {
    loadProducts();
    loadCategories();
    loadProductCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh if the catalog tab was in background for a while or if the
  // product list ends up unexpectedly empty. Prevents "no products found until
  // manual refresh" reported by customers after leaving the tab idle.
  useEffect(() => {
    let hiddenAt = null;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }
      if (document.visibilityState === 'visible') {
        const away = hiddenAt ? Date.now() - hiddenAt : 0;
        if (away > 2 * 60 * 1000 || products.length === 0) {
          loadProducts();
        }
        hiddenAt = null;
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products.length]);

  useEffect(() => {
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  useEffect(() => {
    filterProductsBySearch();
    updatePreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, searchTerm, bikeCompatibleSkus]);

  // Resolve the SKUs matching the current bike filter whenever it changes
  useEffect(() => {
    const { brand, year, model } = bikeFilter;
    if (!brand) {
      setBikeCompatibleSkus(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const params = { brand };
        if (year) params.year = year;
        if (model) params.model = model;
        const r = await axios.get(`${BACKEND_URL}/api/bikes/products`, { params });
        if (!cancelled) {
          const list = Array.isArray(r.data?.products) ? r.data.products : [];
          setBikeCompatibleSkus(new Set(list.map((p) => p.sku)));
        }
      } catch (_) {
        if (!cancelled) setBikeCompatibleSkus(new Set());
      }
    })();
    return () => { cancelled = true; };
  }, [bikeFilter.brand, bikeFilter.year, bikeFilter.model]);

  const filterProductsBySearch = () => {
    // Start from the full catalog, then narrow by bike-compat filter (if any)
    let base = products;
    if (bikeCompatibleSkus instanceof Set) {
      base = base.filter((p) => bikeCompatibleSkus.has(p.sku));
    }

    if (!searchTerm) {
      setFilteredProducts(base);
      return;
    }
    // Filter, then rank so title matches appear before compat-only matches
    const filtered = base
      .filter((p) => matchesProductSearch(p, searchTerm))
      .sort((a, b) => scoreProductSearch(b, searchTerm) - scoreProductSearch(a, searchTerm));
    setFilteredProducts(filtered);
  };

  const updatePreview = () => {
    if (!searchTerm || searchTerm.length < 2) {
      setPreviewResults([]);
      setShowPreview(false);
      return;
    }
    const results = products
      .filter((p) => matchesProductSearch(p, searchTerm))
      .sort((a, b) => scoreProductSearch(b, searchTerm) - scoreProductSearch(a, searchTerm))
      .slice(0, 5); // Show max 5 preview results

    setPreviewResults(results);
    setShowPreview(results.length > 0);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      setShowPreview(false);
      // Scroll to results
      const resultsSection = document.getElementById('products-grid');
      if (resultsSection) {
        resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const handleProductClick = (sku) => {
    setShowPreview(false);
    navigate(getProductPath({ sku, slug: null }));
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const params = selectedCategory !== 'All' ? { category: selectedCategory } : {};
      const response = await axios.get(`${BACKEND_URL}/api/products`, { params });
      // Defensive: never overwrite the product list with a non-array (prevents
      // "empty catalog after idle" bug if the API ever returns an error object).
      if (!Array.isArray(response.data)) return;
      setProducts(response.data);
      setFilteredProducts(response.data); // Set filtered initially
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/categories`);
      
      // Fix: Count ALL products, not just categorized ones
      const allProductsResponse = await axios.get(`${BACKEND_URL}/api/products`);
      const totalCount = Array.isArray(allProductsResponse.data) ? allProductsResponse.data.length : 0;
      const cats = Array.isArray(response.data) ? response.data : [];
      
      setCategories([
        { name: 'Tutti', count: totalCount },
        ...cats.filter(cat => cat.count > 0)
      ]);
    } catch (error) {
    }
  };

  const loadProductCounts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/categories`);
      if (!Array.isArray(response.data)) return;
      const counts = {};
      response.data.forEach(cat => {
        counts[cat.name] = cat.count;
      });
      setProductCounts(counts);
    } catch (error) {
    }
  };

  // Apply sorting
  const applySorting = (productsList) => {
    if (!sortBy || !productsList) return productsList;
    
    const sorted = [...productsList];
    switch (sortBy) {
      case 'name-asc':
        return sorted.sort((a, b) => (a.name_it || '').localeCompare(b.name_it || ''));
      case 'name-desc':
        return sorted.sort((a, b) => (b.name_it || '').localeCompare(a.name_it || ''));
      case 'price-asc':
        return sorted.sort((a, b) => (a.price || 0) - (b.price || 0));
      case 'price-desc':
        return sorted.sort((a, b) => (b.price || 0) - (a.price || 0));
      default:
        return sorted;
    }
  };

  // Get sorted products for display
  const displayProducts = applySorting(filteredProducts);

  return (
    <div className="dark-container pt-20">
      <SEO 
        title="Catalogo Prodotti Motocross - ALLMX | Ricambi e Accessori Premium"
        description="Sfoglia il nostro catalogo di oltre 300 prodotti per motocross: plastiche, freni, sospensioni, scarichi, trasmissione, protezioni e accessori. Marche top: KTM, Husqvarna, GasGas, Honda, Yamaha. Prezzi competitivi e spedizione veloce."
        keywords="catalogo motocross, prodotti moto cross, ricambi KTM, plastiche Husqvarna, freni moto, sospensioni enduro, ALLMX shop"
        url="https://allmx.it/products"
      />
      {/* Page Header */}
      <section className="py-20 px-6 lg:px-[7.6923%]" style={{ backgroundColor: 'var(--bg-secondary)' }}>
        <div className="max-w-[1400px] mx-auto text-center">
          <h1 className="display-large mb-4">{t.products.title}</h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
            {t.products.subtitle}
          </p>
        </div>
      </section>

      {/* Search Bar */}
      <section className="px-6 lg:px-[7.6923%] pt-8">
        <div className="max-w-[1400px] mx-auto">
          <div className="relative max-w-2xl mx-auto">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => handleSearchTermChange(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              onFocus={() => searchTerm.length >= 2 && setShowPreview(true)}
              onBlur={() => setTimeout(() => setShowPreview(false), 200)}
              placeholder={language === 'it' ? '🔍 Cerca per nome o codice prodotto...' : '🔍 Search by name or product code...'}
              className="w-full bg-white text-black border border-gray-300 rounded px-4 py-3 pl-12 focus:outline-none focus:border-primary transition-colors text-base placeholder-gray-500"
            />
            <svg 
              className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" 
              width="20" 
              height="20" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.35-4.35"/>
            </svg>
            {searchTerm && (
              <button
                onClick={() => {
                  handleSearchTermChange('');
                  setShowPreview(false);
                }}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 text-xl"
              >
                ✕
              </button>
            )}
            
            {/* Preview Dropdown */}
            {showPreview && previewResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-[#0a0a0a] border border-white/10 rounded-lg shadow-2xl max-h-[400px] overflow-y-auto z-50">
                {previewResults.map((product) => (
                  <div
                    key={product.sku}
                    onClick={() => handleProductClick(product.sku)}
                    className="flex items-center gap-4 p-3 hover:bg-white/5 cursor-pointer transition-colors border-b border-white/5 last:border-0"
                  >
                    <div className="w-16 h-16 flex-shrink-0 bg-white/5 rounded overflow-hidden">
                      {product.images && product.images[0] ? (
                        <img 
                          src={getProxiedImageUrl(product.images[0])}
                          alt={language === 'it' ? product.name_it : product.name_en}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const fallback = document.createElement('div');
                            fallback.className = 'w-full h-full flex items-center justify-center text-white/20 text-xs';
                            fallback.textContent = 'No Image';
                            e.target.parentElement.appendChild(fallback);
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white/20 text-xs">
                          No Image
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">
                        {language === 'it' ? product.name_it : product.name_en}
                      </div>
                      <div className="text-sm text-white/60">
                        SKU: {product.sku}
                        {product.brand && ` • ${product.brand}`}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-lg font-bold" style={{ color: 'var(--brand-primary)' }}>
                        €{product.price.toFixed(2)}
                      </div>
                      {/* Smart Badge: in_stock / preorder / out_of_stock */}
                      {product.availability_status === 'preorder' ? (
                        <div className="text-xs text-orange-500">
                          {language === 'it' ? 'Su ordinazione' : 'Preorder'}
                        </div>
                      ) : product.stock > 0 ? (
                        <div className="text-xs text-green-500">
                          {language === 'it' ? 'Disponibile' : 'Available'}
                        </div>
                      ) : (
                        <div className="text-xs text-red-500">
                          {language === 'it' ? 'Esaurito' : 'Out of Stock'}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                <div className="p-3 text-center text-sm text-white/60 bg-white/5">
                  {language === 'it' 
                    ? `Premi Invio per vedere tutti i ${filteredProducts.length} risultati` 
                    : `Press Enter to see all ${filteredProducts.length} results`}
                </div>
              </div>
            )}
          </div>
          {searchTerm && !showPreview && (
            <div className="mt-3 text-center text-sm text-white/60">
              {language === 'it' 
                ? `${filteredProducts.length} risultati per "${searchTerm}"` 
                : `${filteredProducts.length} results for "${searchTerm}"`}
            </div>
          )}
        </div>
      </section>


      {/* Bike finder quick filter — above the categories */}
      <section className="px-6 lg:px-[7.6923%] pt-4">
        <div className="max-w-[1400px] mx-auto">
          <BikeFilter
            value={bikeFilter}
            onChange={setBikeFilter}
            variant="inline"
          />
        </div>
      </section>


      {/* Category Icons Section - Visual Category Selection */}
      <section className="py-8 px-6 lg:px-[7.6923%] overflow-hidden">
        <div className="max-w-[1400px] mx-auto">
          <h2 className="text-2xl font-bold mb-6 text-center" style={{ color: 'var(--brand-primary)' }}>
            {language === 'it' ? 'Esplora per Categoria' : language === 'es' ? 'Explorar por Categoría' : 'Browse by Category'}
          </h2>
          <div className="flex items-center gap-4 md:gap-6 mb-8 overflow-x-auto pb-4 scrollbar-hide"
               style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {/* Tutti/All Category */}
            <button
              onClick={() => setSelectedCategory('All')}
              className={`group flex flex-col items-center gap-2 transition-all flex-shrink-0 w-20 md:w-24 ${
                selectedCategory === 'All' ? 'scale-110' : 'hover:scale-110'
              }`}
            >
              <div 
                className="w-14 h-14 md:w-18 md:h-18 rounded-full flex items-center justify-center transition-all"
                style={{ 
                  backgroundColor: selectedCategory === 'All' ? '#FF6600' : 'rgba(255, 102, 0, 0.1)',
                  border: selectedCategory === 'All' ? '3px solid #FF6600' : '2px solid rgba(255, 102, 0, 0.3)',
                  boxShadow: selectedCategory === 'All' ? '0 0 20px rgba(255, 102, 0, 0.5)' : 'none'
                }}
              >
                <Filter 
                  size={28} 
                  style={{ color: selectedCategory === 'All' ? '#000' : '#FF6600' }}
                />
              </div>
              <span 
                className="text-xs md:text-sm font-medium text-center leading-tight"
                style={{ 
                  color: selectedCategory === 'All' ? '#FF6600' : (theme === 'light' ? '#1a1a1a' : '#ffffff'),
                  fontWeight: selectedCategory === 'All' ? 'bold' : 'normal'
                }}
              >
                {language === 'it' ? 'Tutti' : 'All'}
              </span>
              <span className="text-xs" style={{ color: theme === 'light' ? '#666666' : 'rgba(255, 255, 255, 0.6)' }}>
                ({categories.find(c => c.name === 'Tutti')?.count || 0})
              </span>
            </button>

            {/* Category Icons */}
            {allCategories.map((category) => {
              const IconComponent = category.icon;
              const productCount = productCounts[category.name] || 0;
              const categoryName = language === 'it' ? category.name : language === 'es' ? (category.name_es || category.name_en) : category.name_en;
              const isSelected = selectedCategory === category.name;
              
              return (
                <button
                  key={category.name}
                  onClick={() => setSelectedCategory(category.name)}
                  className={`group flex flex-col items-center gap-2 transition-all flex-shrink-0 w-20 md:w-24 ${
                    isSelected ? 'scale-110' : 'hover:scale-110'
                  }`}
                >
                  <div 
                    className="w-14 h-14 md:w-18 md:h-18 rounded-full flex items-center justify-center transition-all"
                    style={{ 
                      backgroundColor: isSelected ? '#FF6600' : 'rgba(255, 102, 0, 0.1)',
                      border: isSelected ? '3px solid #FF6600' : '2px solid rgba(255, 102, 0, 0.3)',
                      boxShadow: isSelected ? '0 0 20px rgba(255, 102, 0, 0.5)' : 'none'
                    }}
                  >
                    <IconComponent 
                      size={28} 
                      style={{ color: isSelected ? '#000' : '#FF6600' }}
                      className="transition-transform group-hover:scale-110"
                    />
                  </div>
                  <span 
                    className="text-xs md:text-sm font-medium text-center leading-tight"
                    style={{ 
                      color: isSelected ? '#FF6600' : (theme === 'light' ? '#1a1a1a' : '#ffffff'),
                      fontWeight: isSelected ? 'bold' : 'normal'
                    }}
                  >
                    {categoryName}
                  </span>
                  <span className="text-xs" style={{ color: theme === 'light' ? '#666666' : 'rgba(255, 255, 255, 0.6)' }}>
                    ({productCount})
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 px-6 lg:px-[7.6923%] border-b border-white/25">
        <div className="max-w-[1400px] mx-auto">
          {/* Sorting Dropdown */}
          <div className="flex items-center justify-between">
            <div className="text-sm text-white/60">
              {language === 'it' 
                ? `${filteredProducts.length} prodotti` 
                : `${filteredProducts.length} products`}
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-white/10 border border-white/20 rounded px-4 py-2 text-white focus:outline-none focus:border-primary transition-colors"
            >
              <option value="" className="bg-black">
                {language === 'it' ? 'Ordina per' : 'Sort by'}
              </option>
              <option value="name-asc" className="bg-black">
                {language === 'it' ? 'Nome A-Z' : 'Name A-Z'}
              </option>
              <option value="name-desc" className="bg-black">
                {language === 'it' ? 'Nome Z-A' : 'Name Z-A'}
              </option>
              <option value="price-asc" className="bg-black">
                {language === 'it' ? 'Prezzo: basso → alto' : 'Price: low → high'}
              </option>
              <option value="price-desc" className="bg-black">
                {language === 'it' ? 'Prezzo: alto → basso' : 'Price: high → low'}
              </option>
            </select>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section id="products-grid" className="py-20 px-6 lg:px-[7.6923%]">
        <div className="max-w-[1400px] mx-auto">
          {loading ? (
            <div className="flex justify-center items-center min-h-[400px]">
              <Loader size={48} className="animate-spin" style={{ color: 'var(--brand-primary)' }} />
            </div>
          ) : displayProducts.length === 0 ? (
            <div className="text-center py-20">
              <p className="body-large" style={{ color: 'var(--text-muted)' }}>
                {language === 'it' ? 'Nessun prodotto trovato' : 'No products found'}
              </p>
            </div>
          ) : (
            <>
              {/* Breadcrumbs */}
              <Breadcrumbs 
                items={
                  selectedCategory !== 'All' 
                    ? [{ label: language === 'it' ? 'Prodotti' : 'Products', url: '/products' }, { label: selectedCategory }]
                    : [{ label: language === 'it' ? 'Prodotti' : 'Products' }]
                }
              />

              <div className="mb-6 text-lg">
                {language === 'it' 
                  ? `${displayProducts.length} prodotti trovati` 
                  : `${displayProducts.length} products found`}
              </div>
              <div className="dark-grid">
                {displayProducts.map((product) => (
                  <Link 
                    key={product.sku} 
                    to={getProductPath(product)}
                    className="dark-card overflow-hidden block hover:transform hover:scale-[1.02] transition-transform"
                  >
                    <div className="aspect-square overflow-hidden bg-white relative">
                      {/* Wishlist & Compare buttons - Top corners */}
                      <div className="absolute top-2 left-2 z-10 flex gap-2">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            isInWishlist(product.sku) 
                              ? removeFromWishlist(product.sku) 
                              : addToWishlist(product);
                          }}
                          className={`p-2 rounded-full transition-all ${
                            isInWishlist(product.sku)
                              ? 'bg-red-500 text-white'
                              : 'bg-white/90 text-gray-700 hover:bg-red-500 hover:text-white'
                          }`}
                          title={language === 'it' ? 'Aggiungi ai preferiti' : 'Add to wishlist'}
                        >
                          <Heart size={18} fill={isInWishlist(product.sku) ? 'currentColor' : 'none'} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            isInCompare(product.sku)
                              ? removeFromCompare(product.sku)
                              : addToCompare(product);
                          }}
                          className={`p-2 rounded-full transition-all ${
                            isInCompare(product.sku)
                              ? 'bg-primary text-black'
                              : 'bg-white/90 text-gray-700 hover:bg-primary hover:text-black'
                          }`}
                          title={language === 'it' ? 'Confronta' : 'Compare'}
                        >
                          <Scale size={18} />
                        </button>
                      </div>
                      
                      {/* Badge ESAURITO/PREORDINE sull'immagine */}
                      {product.stock === 0 ? (
                        <div className="absolute top-4 right-4 z-10 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg">
                          {language === 'it' ? 'ESAURITO' : 'OUT OF STOCK'}
                        </div>
                      ) : product.availability_status === 'preorder' ? (
                        <div className="absolute top-4 right-4 z-10 bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg">
                          {language === 'it' ? 'SU ORDINAZIONE' : 'PREORDER'}
                        </div>
                      ) : null}
                      {product.images && product.images.length > 0 ? (
                        <img
                          src={getProxiedImageUrl(product.images[0])}
                          alt={language === 'it' ? product.name_it : product.name_en}
                          className={`w-full h-full object-contain p-4 dark-transition hover:scale-110 ${product.stock === 0 ? 'opacity-60' : ''}`}
                          loading="lazy"
                          style={{ backgroundColor: '#ffffff' }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white/20">
                          {language === 'it' ? 'Immagine non disponibile' : 'No Image'}
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      <div className="text-sm mb-2" style={{ color: 'var(--brand-primary)' }}>
                        {product.category}
                      </div>
                      <h3 className="text-xl font-semibold mb-3">
                        {language === 'it' ? product.name_it : product.name_en}
                      </h3>
                      <p className="body-medium mb-4 line-clamp-2">
                        {language === 'it' 
                          ? (product.description_it || '').substring(0, 150) + '...'
                          : (product.description_en || '').substring(0, 150) + '...'}
                      </p>
                      <div className="flex items-start justify-between mb-4 gap-2 flex-wrap">
                        <div className="text-2xl md:text-3xl font-bold flex-shrink min-w-0 break-words" style={{ color: 'var(--brand-primary)' }}>
                          €{product.price ? product.price.toFixed(2) : '0.00'}
                        </div>
                        {/* Smart Badge: in_stock / preorder / out_of_stock with urgency for low stock */}
                        {product.availability_status === 'preorder' ? (
                          <div className="text-xs md:text-sm px-2 md:px-3 py-1 bg-orange-500/20 text-orange-400 font-semibold whitespace-nowrap flex-shrink-0">
                            {language === 'it' ? 'Su ordinazione' : 'Preorder'}
                          </div>
                        ) : product.stock > 0 ? (
                          <div className={`text-xs md:text-sm px-2 md:px-3 py-1 font-semibold whitespace-nowrap flex-shrink-0 ${
                            product.stock < 10 
                              ? 'bg-orange-500/20 text-orange-400' 
                              : 'bg-green-500/20 text-green-400'
                          }`}>
                            {product.stock < 10 
                              ? (language === 'it' ? `Solo ${product.stock}!` : `Only ${product.stock}!`)
                              : (language === 'it' ? 'Disponibile' : 'In Stock')
                            }
                          </div>
                        ) : (
                          <div className="text-xs md:text-sm px-2 md:px-3 py-1 bg-red-500/20 text-red-400 whitespace-nowrap flex-shrink-0">
                            {language === 'it' ? 'Esaurito' : 'Out of Stock'}
                          </div>
                        )}
                      </div>
                      
                      {/* Add to Cart Button */}
                      <button 
                        className={`w-full mb-3 py-3 px-4 rounded font-semibold transition-all flex items-center justify-center gap-2 ${
                          product.stock === 0 
                            ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                            : 'btn-primary'
                        }`}
                        disabled={product.stock === 0}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (product.stock > 0) {
                            addToCart(product);
                          }
                        }}
                      >
                        <ShoppingCart size={20} />
                        {product.stock === 0 
                          ? (language === 'it' ? 'Non Disponibile' : 'Unavailable')
                          : (language === 'it' ? 'Aggiungi al Carrello' : 'Add to Cart')
                        }
                      </button>

                      {/* Also available on marketplaces (secondary) */}
                      <div className="text-xs text-center" style={{ color: 'var(--text-muted)' }}>
                        {language === 'it' ? 'Disponibile anche su' : 'Also available on'}{' '}
                        <span className="text-white/50">Amazon & eBay</span>
                      </div>

                      {product.brand && (
                        <div className="mt-3 text-sm" style={{ color: 'var(--text-muted)' }}>
                          {language === 'it' ? 'Marca' : 'Brand'}: <span className="font-semibold">{product.brand}</span>
                        </div>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Products;
